<template>
	<div class="charts-wrapper">
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('total_orders')"
			:caption="getString('total')"
			:value="totalSales"
			:last-period-value="totalSalesPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="salesSeries"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('orders_volume')"
			:caption="getString('orders')"
			:value="totalOrders"
			:last-period-value="totalOrdersPrevious"
			:colors="['#dadada', '#0752A3']"
			:series="ordersSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			type="area"
			:caption="bestModeName"
			:value="bestModeValue"
			:last-period-value="bestModeValuePrevious"
			:title="getString('volume_mode_chart')"
			:colors="modeSalesSeries.map(series => series.color)"
			:series="modeSalesSeries"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			type="area"
			:caption="bestModeNameOrders"
			:value="bestModeOrders"
			:last-period-value="bestModeOrdersPrevious"
			:title="getString('orders_mode_chart')"
			:colors="modeOrdersSeries.map(series => series.color)"
			:series="modeOrdersSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			type="area"
			:caption="bestTypeName"
			:value="bestTypeValue"
			:last-period-value="bestTypeValuePrevious"
			:title="getString('volume_types_chart')"
			:colors="typeSalesSeries.map(series => series.color)"
			:series="typeSalesSeries"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			type="area"
			:caption="bestTypeNameOrders"
			:value="bestTypeOrders"
			:last-period-value="bestTypeOrdersPrevious"
			:title="getString('orders_types_chart')"
			:colors="typeOrdersSeries.map(series => series.color)"
			:series="typeOrdersSeries"
			:xaxis="xaxisLabels"
			no-currency
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			type="area"
			:caption="bestStaffName"
			:value="bestStaffValue"
			:last-period-value="bestStaffValuePrevious"
			:title="getString(staffViewMode === 'creation' ? 'staff_volume_chart' : 'station_volume_chart')"
			:colors="staffSalesSeries.map(series => series.color)"
			:series="staffSalesSeries"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		>
			<template v-slot:custom-header="{ expanded, clickExpand, expandible, isEmpty }">
				<h3>{{ getString(staffViewMode === 'creation' ? 'staff_volume_chart' : 'station_volume_chart') }}</h3>
				<div style="display: flex; gap: 10px; align-items: center;">
					<AppButtonIcon
						:icon-name="staffViewMode === 'creation' ? 'Icon-User-blue' : 'icon-money-blue'"
						:title="staffViewMode === 'creation' ? getString('order_creation') : getString('payment_side')"
						prevent-change
						color="var(--color-bg-primary-1)"
						@click="toggleStaffView"
					/>
					<AppButtonIcon 
						v-if="expandible && !isEmpty" 
						class="expand-icon" 
						:active="expanded" 
						prevent-change 
						color="var(--color-bg-primary-1)" 
						:icon-name="'icon-'+(expanded?'compress':'expand')+'-blue'" 
						@click="clickExpand" 
					/>
				</div>
			</template>
		</AppChart>
		<AppChart
			expandible
			height="100%"
			full-width
			:top-scroll-diff="selectorsRowHeight"
			:title="getString('weather')"
			:caption="getString('temperature')"
			:value="weatherSeriesValue"
			:last-period-value="null"
			:colors="['#834645', '#74ccf4', '#0752A3']"
			:series="weatherSeries"
			type="rangeArea"
			:xaxis="xaxisLabels"
			:formatter="r => currencyStore.readableCurrencyValue(r, false, true, true)"
			:dark="userStore.darkMode"
			:force-y-axis-zero="true"
		/>
	</div>
</template>

<script>
import AppChart from "@/shared/components/AppChart.vue";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import {useAnalyticsStore, useMenuStore, useUserStore} from "@/console-new/stores";
import {useCurrencyStore, useLanguageStore, useValuesStore} from "@/shared/stores";
import WindowWidth from "@/console-new/mixins/WindowWidth";
import AppTable from "@/shared/components/AppTable.vue";

export default {
	setup() {
		const analyticsStore = useAnalyticsStore();
		const languageStore = useLanguageStore();
		const menuStore = useMenuStore();
		const valuesStore = useValuesStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {menuStore, userStore, valuesStore, analyticsStore, currencyStore, languageStore, getString: languageStore.getString};
	},
	name: "OrdersChartsAggregated",
	components: {AppTable, AppChart, AppButtonIcon},
	mixins: [WindowWidth],
	props: {
		dates: {
			type: Object,
			required: true,
			validator: (value) => {
				return value.hasOwnProperty("start") && value.hasOwnProperty("end") && value.hasOwnProperty("previousStart") && value.hasOwnProperty("previousEnd") && value.hasOwnProperty("timeframeIndex");
			}
		},
	},
	data() {
		return {
			staffViewMode: 'creation', // 'creation' or 'payment'
		};
	},
	computed: {
		ordersModes() {
			return this.valuesStore.ordersModes;
		},
		selectorsRowHeight() {
			return this.windowWidth > 820 ? 80 : this.windowWidth > 420 ? 150 : 190;
		},
		aggregatedData() {
			return this.analyticsStore.ordersAggregated;
		},
		xaxisLabels() {
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			return xaxisTimes.map(time => time.label);
		},
		adjustedDates() {
			if (this.dates.timeframeIndex === 2 || this.dates.timeframeIndex === 6) {
				const now = new Date();
				let adjustedStart, adjustedEnd, adjustedPreviousStart, adjustedPreviousEnd;
				
				if (this.dates.timeframeIndex === 2) {
					adjustedStart = this.analyticsStore.firstDayOfCurrentMonth();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastMonth();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				} else {
					adjustedStart = this.analyticsStore.firstDayOfCurrentYear();
					adjustedEnd = now;
					const elapsedTime = adjustedEnd.getTime() - adjustedStart.getTime();
					adjustedPreviousStart = this.analyticsStore.firstDayOfLastYear();
					adjustedPreviousEnd = new Date(adjustedPreviousStart.getTime() + elapsedTime);
				}
				
				return {
					...this.dates,
					start: adjustedStart,
					end: adjustedEnd,
					previousStart: adjustedPreviousStart,
					previousEnd: adjustedPreviousEnd
				};
			}
			return this.dates;
		},
		totalSales() {
			return this.calculateTotal(this.aggregatedData.full, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalSalesPrevious() {
			return this.calculateTotal(this.aggregatedData.full, 'gross_sales', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		totalOrders() {
			return this.calculateTotal(this.aggregatedData.full, 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
		},
		totalOrdersPrevious() {
			return this.calculateTotal(this.aggregatedData.full, 'orders_cnt', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
		},
		salesSeries() {
			const previousData = this.buildTimeSeries(this.aggregatedData.full, 'gross_sales', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.full, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		ordersSeries() {
			const previousData = this.buildTimeSeries(this.aggregatedData.full, 'orders_cnt', this.adjustedDates.previousStart, this.adjustedDates.previousEnd);
			const currentData = this.buildTimeSeries(this.aggregatedData.full, 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			
			// Check if all values are 0
			const hasData = previousData.some(v => v > 0) || currentData.some(v => v > 0);
			if (!hasData) {
				return [];
			}
			
			return [
				{
					name: this.getString('previous_period'),
					data: previousData
				},
				{
					name: this.getString('current_period'),
					data: currentData
				}
			];
		},
		modeSalesSeries() {
			return this.buildDimensionSeries(this.aggregatedData.mode, 'order_mode', 'gross_sales', this.ordersModes);
		},
		modeOrdersSeries() {
			return this.buildDimensionSeries(this.aggregatedData.mode, 'order_mode', 'orders_cnt', this.ordersModes);
		},
		bestModeName() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.ordersModes[best]?.name || best : '';
		},
		bestModeValue() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.mode, 'order_mode', best, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestModeValuePrevious() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.mode, 'order_mode', best, 'gross_sales', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		bestModeNameOrders() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.ordersModes[best]?.name || best : '';
		},
		bestModeOrders() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.mode, 'order_mode', best, 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestModeOrdersPrevious() {
			const best = this.findBestDimension(this.aggregatedData.mode, 'order_mode', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.mode, 'order_mode', best, 'orders_cnt', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		typeSalesSeries() {
			return this.buildDimensionSeries(this.aggregatedData.type, 'payment_type', 'gross_sales', this.valuesStore.paymentsMethods);
		},
		typeOrdersSeries() {
			return this.buildDimensionSeries(this.aggregatedData.type, 'payment_type', 'orders_cnt', this.valuesStore.paymentsMethods);
		},
		bestTypeName() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.valuesStore.paymentsMethods[best]?.name || best : '';
		},
		bestTypeValue() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.type, 'payment_type', best, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestTypeValuePrevious() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.type, 'payment_type', best, 'gross_sales', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		bestTypeNameOrders() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.valuesStore.paymentsMethods[best]?.name || best : '';
		},
		bestTypeOrders() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.type, 'payment_type', best, 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestTypeOrdersPrevious() {
			const best = this.findBestDimension(this.aggregatedData.type, 'payment_type', 'orders_cnt', this.adjustedDates.start, this.adjustedDates.end);
			return best ? this.calculateDimensionTotal(this.aggregatedData.type, 'payment_type', best, 'orders_cnt', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		staffSalesSeries() {
			const staffMap = this.buildStaffMap();
			const dimension = this.staffViewMode === 'creation' ? this.aggregatedData.staff : this.aggregatedData.payment_user;
			const idField = this.staffViewMode === 'creation' ? 'staff_user_id' : 'payment_user_id';
			return this.buildDimensionSeries(dimension, idField, 'gross_sales', staffMap);
		},
		bestStaffName() {
			const dimension = this.staffViewMode === 'creation' ? this.aggregatedData.staff : this.aggregatedData.payment_user;
			const idField = this.staffViewMode === 'creation' ? 'staff_user_id' : 'payment_user_id';
			const best = this.findBestDimension(dimension, idField, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			if (best === null) return '';
			if (best === "null") return this.getString('online_client');
			const user = this.aggregatedData.users.find(u => u.user_id == best);
			return user ? user.name : this.getString('online_client');
		},
		bestStaffValue() {
			const dimension = this.staffViewMode === 'creation' ? this.aggregatedData.staff : this.aggregatedData.payment_user;
			const idField = this.staffViewMode === 'creation' ? 'staff_user_id' : 'payment_user_id';
			const best = this.findBestDimension(dimension, idField, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best !== null ? this.calculateDimensionTotal(dimension, idField, best, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end) : 0;
		},
		bestStaffValuePrevious() {
			const dimension = this.staffViewMode === 'creation' ? this.aggregatedData.staff : this.aggregatedData.payment_user;
			const idField = this.staffViewMode === 'creation' ? 'staff_user_id' : 'payment_user_id';
			const best = this.findBestDimension(dimension, idField, 'gross_sales', this.adjustedDates.start, this.adjustedDates.end);
			return best !== null ? this.calculateDimensionTotal(dimension, idField, best, 'gross_sales', this.adjustedDates.previousStart, this.adjustedDates.previousEnd) : 0;
		},
		weatherSeries() {
			if (!this.salesSeries || this.salesSeries.length === 0 || !this.analyticsStore.weather || this.analyticsStore.weather.length === 0) {
				return [];
			}
			
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);
			const result = [];
			
			const zeroSeries = xaxisTimes.map(() => 0);
			const temperature = {
				name: this.getString('temperature'),
				type: 'rangeArea',
				data: zeroSeries.map((v, i) => ({
					x: i + 1,
					y: [0, 0]
				}))
			};
			const precipitation = {
				name: this.getString('rain'),
				type: 'area',
				data: zeroSeries.map((v, i) => ({
					x: i + 1,
					y: 0
				}))
			};
			const orders = {
				name: this.getString('orders'),
				type: 'line',
				data: this.salesSeries && this.salesSeries[1] ? this.salesSeries[1].data.map((v, i) => ({
					x: i + 1,
					y: v
				})) : []
			};
			
			const indexValues = {};
			this.analyticsStore.weather.forEach(weatherPoint => {
				const datesIndex = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, weatherPoint);
				
				if (datesIndex !== -1) {
					if (!indexValues[datesIndex]) {
						indexValues[datesIndex] = {
							temperatureMin: null,
							temperatureMax: null,
							precipitation: 0,
							count: 0
						};
					}
					indexValues[datesIndex].temperatureMin = indexValues[datesIndex].temperatureMin === null ? 
						weatherPoint.temperatureMin : Math.min(indexValues[datesIndex].temperatureMin, weatherPoint.temperatureMin);
					indexValues[datesIndex].temperatureMax = indexValues[datesIndex].temperatureMax === null ? 
						weatherPoint.temperatureMax : Math.max(indexValues[datesIndex].temperatureMax, weatherPoint.temperatureMax);
					indexValues[datesIndex].precipitation += weatherPoint.precipitation;
					indexValues[datesIndex].count++;
				}
			});
			
			const days = this.analyticsStore.numberOfDaysInTimeframePoint(this.adjustedDates);
			for (let i = 0; i < temperature.data.length; i++) {
				if (indexValues[i]) {
					temperature.data[i].y = [indexValues[i].temperatureMin, indexValues[i].temperatureMax];
					precipitation.data[i].y = indexValues[i].precipitation / days;
				}
			}
			
			for (let i = 1; i < temperature.data.length; i++) {
				if (temperature.data[i].y[0] === 0 && temperature.data[i].y[1] === 0) {
					temperature.data[i].y = temperature.data[i - 1].y;
				}
			}
			for (let i = temperature.data.length - 2; i >= 0; i--) {
				if (temperature.data[i].y[0] === 0 && temperature.data[i].y[1] === 0) {
					temperature.data[i].y = temperature.data[i + 1].y;
				}
			}
			
			result.push(temperature);
			result.push(precipitation);
			result.push(orders);
			return result;
		},
		weatherSeriesValue() {
			if (!this.weatherSeries || this.weatherSeries.length === 0 || !this.weatherSeries[0].data || this.weatherSeries[0].data.length === 0) {
				return "-";
			}
			let min = null;
			let max = null;
			
			this.weatherSeries[0].data.forEach(point => {
				if (min === null || point.y[0] < min) {
					min = point.y[0];
				}
				if (max === null || point.y[1] > max) {
					max = point.y[1];
				}
			});
			
			if (min === null || max === null) {
				return "-";
			}
			return min.toFixed(1) + "° - " + max.toFixed(1) + "°";
		},
	},
	methods: {
		calculateTotal(data, field, startDate, endDate) {
			if (!data || data.length === 0) return 0;

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const isPrevious = startDate < this.adjustedDates.start;

			const filtered = data.filter(row => {
				// Period filter: if row has _period tag, it must match
				if (row._period) {
					const expectedPeriod = isPrevious ? 'previous' : 'current';
					if (row._period !== expectedPeriod) return false;
				}

				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				if (bucketColumn === 'month_bucket') {
					// For month buckets, compare at month level
					const bucketMonth = date.getFullYear() * 12 + date.getMonth();
					const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
					const endMonth = endDate.getFullYear() * 12 + endDate.getMonth();
					return bucketMonth >= startMonth && bucketMonth <= endMonth;
				}
				return date >= startDate && date <= endDate;
			});
			
			const total = filtered.reduce((sum, row) => sum + Number(row[field] || 0), 0);
			const isMonetaryField = field.includes('gross_sales') || field.includes('payments');
			return isMonetaryField ? total / 100 : total;
		},
		buildTimeSeries(data, field, startDate, endDate) {
			if (!data || data.length === 0) return [];

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);

			const series = new Array(xaxisTimes.length).fill(0);

			const isPrevious = startDate < this.adjustedDates.start;

			const filtered = data.filter(row => {
				// Period filter: if row has _period tag, it must match
				if (row._period) {
					const expectedPeriod = isPrevious ? 'previous' : 'current';
					if (row._period !== expectedPeriod) return false;
				}

				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				if (bucketColumn === 'month_bucket') {
					// For month buckets, compare at month level
					// The bucket date is the 1st of the month, but we want to include
					// months that overlap with the date range
					const bucketMonth = date.getFullYear() * 12 + date.getMonth();
					const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
					const endMonth = endDate.getFullYear() * 12 + endDate.getMonth();
					return bucketMonth >= startMonth && bucketMonth <= endMonth;
				}
				return date >= startDate && date <= endDate;
			});
			
			filtered.forEach((row, i) => {
				const bucketDate = new Date(row[bucketColumn]);
				
				if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
					bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
				}
				
				const mockOrder = { date: bucketDate };
				const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, isPrevious);
				
				if (index >= 0 && index < series.length) {
					const value = Number(row[field] || 0);
					series[index] += field === 'gross_sales' ? value / 100 : value;
				}
			});
			
			return series;
		},
		buildDimensionSeries(data, dimensionField, valueField, dimensionMap) {
			if (!data || data.length === 0) return [];

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';
			const xaxisTimes = this.analyticsStore.xaxisTimesAggregated(this.adjustedDates);

			const currentData = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(this.adjustedDates.end);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= this.adjustedDates.start && date <= adjustedEndDate;
				}
				if (bucketColumn === 'month_bucket') {
					// For month buckets, compare at month level
					const bucketMonth = date.getFullYear() * 12 + date.getMonth();
					const startMonth = this.adjustedDates.start.getFullYear() * 12 + this.adjustedDates.start.getMonth();
					const endMonth = this.adjustedDates.end.getFullYear() * 12 + this.adjustedDates.end.getMonth();
					return bucketMonth >= startMonth && bucketMonth <= endMonth;
				}
				return date >= this.adjustedDates.start && date <= this.adjustedDates.end;
			});
			
			const dimensions = [...new Set(currentData.map(row => row[dimensionField]))];
			
			const allSeries = dimensions.map(dim => {
				const seriesData = new Array(xaxisTimes.length).fill(0);
				
				const dimData = currentData.filter(row => row[dimensionField] === dim);
				
				dimData.forEach(row => {
					const bucketDate = new Date(row[bucketColumn]);
					if (xaxisTimes.length > 0 && bucketColumn !== 'hour_bucket') {
						bucketDate.setHours(xaxisTimes[0].rawDate.getHours(), xaxisTimes[0].rawDate.getMinutes(), 0, 0);
					}
					
					const mockOrder = { date: bucketDate };
					const index = this.analyticsStore.getDatesIndex(this.adjustedDates, xaxisTimes, mockOrder, false);
					
					if (index >= 0 && index < seriesData.length) {
						const value = Number(row[valueField] || 0);
						const isMonetaryField = valueField.includes('gross_sales') || valueField.includes('payments');
						seriesData[index] += isMonetaryField ? value / 100 : value;
					}
				});
				
				const name = dimensionMap[dim]?.name || dim || this.getString('online_client');
				const color = dimensionMap[dim]?.color || '#999999';
				
				const total = seriesData.reduce((sum, val) => sum + val, 0);
				
				return {
					name: name,
					data: seriesData,
					dimensionValue: dim,
					color: color,
					total: total
				};
			});
			
			return allSeries
				.sort((a, b) => b.total - a.total)
				.slice(0, 6)
				.map(series => {
					const { total, ...seriesWithoutTotal } = series;
					return seriesWithoutTotal;
				});
		},
		findBestDimension(data, dimensionField, valueField, startDate, endDate) {
			if (!data || data.length === 0) return null;

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';

			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate;
				}
				if (bucketColumn === 'month_bucket') {
					// For month buckets, compare at month level
					const bucketMonth = date.getFullYear() * 12 + date.getMonth();
					const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
					const endMonth = endDate.getFullYear() * 12 + endDate.getMonth();
					return bucketMonth >= startMonth && bucketMonth <= endMonth;
				}
				return date >= startDate && date <= endDate;
			});
			
			const dimensionTotals = {};
			filtered.forEach(row => {
				const dim = row[dimensionField];
				if (!dimensionTotals[dim]) dimensionTotals[dim] = 0;
				dimensionTotals[dim] += Number(row[valueField] || 0);
			});
			
			let bestDim = null;
			let bestValue = 0;
			Object.entries(dimensionTotals).forEach(([dim, value]) => {
				if (value > bestValue) {
					bestValue = value;
					bestDim = dim;
				}
			});
			
			return bestDim;
		},
		calculateDimensionTotal(data, dimensionField, dimensionValue, valueField, startDate, endDate) {
			if (!data || data.length === 0) return 0;

			const bucketColumn = this.aggregatedData.bucketColumn || 'day_bucket';

			const filtered = data.filter(row => {
				const date = new Date(row[bucketColumn]);
				const dimValue = dimensionValue === "null" ? null : dimensionValue;
				if (bucketColumn === 'hour_bucket') {
					const adjustedEndDate = new Date(endDate);
					adjustedEndDate.setTime(adjustedEndDate.getTime() + 3600 * 1000);
					return date >= startDate && date <= adjustedEndDate && row[dimensionField] == dimValue;
				}
				if (bucketColumn === 'month_bucket') {
					// For month buckets, compare at month level
					const bucketMonth = date.getFullYear() * 12 + date.getMonth();
					const startMonth = startDate.getFullYear() * 12 + startDate.getMonth();
					const endMonth = endDate.getFullYear() * 12 + endDate.getMonth();
					return bucketMonth >= startMonth && bucketMonth <= endMonth && row[dimensionField] == dimValue;
				}
				return date >= startDate && date <= endDate && row[dimensionField] == dimValue;
			});
			
			const total = filtered.reduce((sum, row) => sum + Number(row[valueField] || 0), 0);
			const isMonetaryField = valueField.includes('gross_sales') || valueField.includes('payments');
			return isMonetaryField ? total / 100 : total;
		},
		buildStaffMap() {
			const staffMap = {};
			this.aggregatedData.users.forEach(user => {
				staffMap[user.user_id] = {
					name: user.name,
					color: this.analyticsStore.bestColors[Object.keys(staffMap).length % this.analyticsStore.bestColors.length]
				};
			});
			staffMap[null] = {
				name: this.getString('online'),
				color: '#0752A3'
			};
			return staffMap;
		},
		toggleStaffView() {
			this.staffViewMode = this.staffViewMode === 'creation' ? 'payment' : 'creation';
		}
	}
};
</script>

<style lang="scss" scoped>
.charts-wrapper {
	display: flex;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	margin-bottom: calc(-1 * var(--main-padding));

	.chart-container {
		min-width: calc((100% - var(--main-padding)) / 2);
		max-width: calc((100% - var(--main-padding)) / 2);
		margin-bottom: var(--main-padding);

		&:deep(.chart-svg-container) {
			transition: min-height .35s ease-in-out;
			height: 200px;
		}

		&.expanded {
			min-width: 100%;
			max-width: 100%;

			&:deep(.chart-svg-container) {
				height: 350px;
			}
		}


		&:deep(.card-header h3) {
			font-size: 18px;
		}

	}

	@media (max-width: 920px) {
		justify-content: space-between;
		.chart-container {
			max-width: calc((100% - var(--main-padding)) / 2);

			&.expanded {
				max-width: 100%;
			}
		}
	}

	@media (max-width: 670px) {
		justify-content: center;
		.chart-container {
			min-width: unset;
			max-width: 100%;
			width: 100%;

			&.expanded {
				max-width: 100%;
			}
		}
	}
}
</style>